import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import {
  Users,
  ShieldAlert,
  Search,
  RefreshCw,
  Clock,
  Activity,
} from "lucide-react";
import "../styles/admin.css";

/**
 * AdminPage Component
 * 
 * Renders the platform administration dashboard allowing administrators to:
 * 1. Read real-time telemetry metrics (total users, active devices, and API rate limits).
 * 2. Search/filter registered user profiles.
 * 3. Inspect detailed profiles (license keys, layouts, and computer pairings).
 * 4. Manually override plans and revoke license keys.
 */
export const AdminPage: React.FC = () => {
  // Search query state for filtering users
  const [searchQuery, setSearchQuery] = useState("");
  // Selected user ID for the detail pane
  const [selectedUserId, setSelectedUserId] = useState<Id<"users"> | null>(null);

  // Form states for manual plan overrides
  const [overridePlan, setOverridePlan] = useState<"free" | "trial" | "pro" | "team">("free");
  const [overrideTrialDays, setOverrideTrialDays] = useState(14);

  // Queries
  const users = useQuery(api.admin.adminListUsers, { search: searchQuery || undefined });
  const systemStatus = useQuery(api.admin.adminGetSystemStatus);
  const selectedUserDetails = useQuery(
    api.admin.adminGetUserDetails,
    selectedUserId ? { userId: selectedUserId } : "skip"
  );
  const rateLimits = useQuery(api.admin.adminGetRateLimits);

  // Mutations
  const updateUserPlan = useMutation(api.admin.adminUpdateUserPlan);
  const revokeLicense = useMutation(api.admin.adminRevokeLicense);

  // Handle plan override submit
  const handlePlanOverrideSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;

    try {
      const trialEndsAt =
        overridePlan === "trial"
          ? Date.now() + overrideTrialDays * 24 * 60 * 60 * 1000
          : undefined;

      await updateUserPlan({
        userId: selectedUserId,
        plan: overridePlan,
        trialEndsAt,
      });

      alert("User plan overridden successfully.");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Error overriding plan: " + errorMessage);
    }
  };

  // Handle license revocation
  const handleRevokeLicense = async (licenseKeyId: Id<"licenseKeys">) => {
    if (!window.confirm("Are you sure you want to revoke this license key? This cannot be undone.")) {
      return;
    }

    try {
      await revokeLicense({ licenseKeyId });
      alert("License key revoked successfully.");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Error revoking license key: " + errorMessage);
    }
  };

  // Render loading state if queries are resolving
  if (users === undefined || systemStatus === undefined) {
    return (
      <div className="admin-container" style={{ alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
        <RefreshCw className="animate-spin" size={48} color="var(--color-accent)" style={{ animation: "spin 1.5s linear infinite" }} />
        <p style={{ color: "var(--color-text-secondary)", marginTop: "1rem" }}>Retrieving platform statistics...</p>
      </div>
    );
  }

  return (
    <div className="admin-container" data-testid="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-title-group">
          <h1>Platform Administration</h1>
          <p className="admin-subtitle">Monitor paired client machines, manage seats, and audit license keys</p>
        </div>
      </header>

      {/* Telemetry Metrics Panel */}
      <section className="telemetry-grid" data-testid="telemetry-panel">
        <div className="telemetry-card">
          <div className="telemetry-icon-wrapper">
            <Users size={24} color="var(--color-accent)" />
          </div>
          <div className="telemetry-info">
            <h4>Total Accounts</h4>
            <div className="telemetry-value" data-testid="telemetry-total-users">
              {systemStatus.totalUsers}
            </div>
          </div>
        </div>

        <div className="telemetry-card">
          <div className="telemetry-icon-wrapper">
            <Activity size={24} color="var(--color-success)" />
          </div>
          <div className="telemetry-info">
            <h4>Active Paired Devices (30s)</h4>
            <div className="telemetry-value" data-testid="telemetry-active-devices">
              {systemStatus.activeComputers30s}
            </div>
          </div>
        </div>

        <div className="telemetry-card">
          <div className="telemetry-icon-wrapper">
            <ShieldAlert size={24} color="var(--color-warning)" />
          </div>
          <div className="telemetry-info">
            <h4>Rate Limited Buckets</h4>
            <div className="telemetry-value" data-testid="telemetry-rate-limits">
              {rateLimits?.length || 0}
            </div>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="search-controls">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search users by name or email address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="user-search-input"
          />
        </div>
      </section>

      {/* Dashboard split content */}
      <div className="admin-content-layout">
        {/* Left pane: Users table */}
        <section className="admin-card" data-testid="users-list-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">User Accounts ({users.length})</h3>
          </div>
          
          <div className="users-table-container">
            {users.length === 0 ? (
              <div className="details-empty-state" data-testid="users-empty-state">
                <Users size={32} />
                <p style={{ marginTop: "1rem" }}>No user accounts found matching query</p>
              </div>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User / Email</th>
                    <th>Plan</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u._id}
                      className={selectedUserId === u._id ? "selected" : ""}
                      onClick={() => {
                        setSelectedUserId(u._id);
                        setOverridePlan(u.plan);
                      }}
                      data-testid={`user-row-${u._id}`}
                    >
                      <td>
                        <div style={{ fontWeight: 600 }}>{u.name || "Anonymous User"}</div>
                        <div style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}>{u.email}</div>
                      </td>
                      <td>
                        <span className={`badge badge-${u.plan}`}>
                          {u.plan}
                        </span>
                        {u.isAdmin && (
                          <span className="badge badge-admin" style={{ marginLeft: "0.25rem" }}>
                            Admin
                          </span>
                        )}
                      </td>
                      <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Right pane: User details or system telemetry log fallback */}
        <section className="admin-card" data-testid="details-card">
          {selectedUserId ? (
            selectedUserDetails === undefined ? (
              <div className="details-empty-state">
                <RefreshCw className="animate-spin" size={24} />
                <p style={{ marginTop: "1rem" }}>Loading user details...</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }} data-testid="user-details-pane">
                <div className="admin-card-header">
                  <h3 className="admin-card-title">User Session & Support</h3>
                  <p className="admin-subtitle" style={{ wordBreak: "break-all" }}>
                    Clerk Subject ID: {selectedUserDetails.user.clerkId}
                  </p>
                </div>

                {/* Overwrite Plan form */}
                <div className="details-section">
                  <h4>Manual Plan Overrides</h4>
                  <form onSubmit={handlePlanOverrideSubmit} className="action-form" data-testid="override-plan-form">
                    <div className="form-group">
                      <label className="form-label">Subscription Tier</label>
                      <select
                        className="form-select"
                        value={overridePlan}
                        onChange={(e) => setOverridePlan(e.target.value as "free" | "trial" | "pro" | "team")}
                        data-testid="plan-select"
                      >
                        <option value="free">Free Plan</option>
                        <option value="trial">Trial Plan</option>
                        <option value="pro">Pro Plan</option>
                        <option value="team">Team Plan</option>
                      </select>
                    </div>

                    {overridePlan === "trial" && (
                      <div className="form-group">
                        <label className="form-label">Extend Trial Duration (Days)</label>
                        <input
                          type="number"
                          className="form-input-number"
                          value={overrideTrialDays}
                          onChange={(e) => setOverrideTrialDays(parseInt(e.target.value) || 1)}
                          min="1"
                          data-testid="trial-days-input"
                        />
                      </div>
                    )}

                    <button type="submit" className="form-btn-submit" data-testid="override-plan-btn">
                      Update User Plan
                    </button>
                  </form>
                </div>

                {/* Paired Computers */}
                <div className="details-section">
                  <h4>Paired Computers ({selectedUserDetails.computers.length})</h4>
                  {selectedUserDetails.computers.length === 0 ? (
                    <p style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>No devices paired yet.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      {selectedUserDetails.computers.map((c) => (
                        <div key={c._id} className="list-item-revoke" style={{ borderLeft: "2px solid var(--color-success)" }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{c.name}</div>
                            <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>
                              Platform: <span style={{ textTransform: "capitalize" }}>{c.platform}</span> | UUID: {c.machineId}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* License Keys */}
                <div className="details-section">
                  <h4>Active Licenses ({selectedUserDetails.licenseKeys.length})</h4>
                  {selectedUserDetails.licenseKeys.length === 0 ? (
                    <p style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>No license keys generated.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      {selectedUserDetails.licenseKeys.map((k) => (
                        <div key={k._id} className="list-item-revoke">
                          <div>
                            <div style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>{k.key}</div>
                            <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>
                              Plan: {k.plan.toUpperCase()} | Status:{" "}
                              <span style={{ color: k.status === "active" ? "var(--color-success)" : "var(--color-error)", fontWeight: 700 }}>
                                {k.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          {k.status === "active" && (
                            <button
                              className="btn-danger"
                              onClick={() => handleRevokeLicense(k._id)}
                              data-testid={`revoke-license-${k._id}`}
                            >
                              Revoke Key
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            /* Fallback system webhook audit trail logs when no user is selected */
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }} data-testid="webhook-logs-pane">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Stripe Webhook Logs</h3>
                <p className="admin-subtitle">Live sync audit of Stripe billing webhooks</p>
              </div>

              {systemStatus.activeWebhookLogs.length === 0 ? (
                <div className="details-empty-state">
                  <Clock size={32} />
                  <p style={{ marginTop: "1rem" }}>No webhook events processed recently</p>
                </div>
              ) : (
                <div className="audit-list">
                  {systemStatus.activeWebhookLogs.map((log) => (
                    <div key={log._id} className="audit-item">
                      <div>
                        <div className="audit-type">{log.eventType}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", marginTop: "0.15rem" }}>
                          Event ID: {log.eventId} | Status:{" "}
                          <span
                            style={{
                              color:
                                log.status === "processed"
                                  ? "var(--color-success)"
                                  : log.status === "failed"
                                    ? "var(--color-error)"
                                    : "var(--color-warning)",
                              fontWeight: 700,
                            }}
                          >
                            {log.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="audit-date">
                        {log.processedAt && new Date(log.processedAt).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminPage;
